/**
 * 🌌 SPIRE DASHBOARD v2.0.0nm
 * Protocol: Sovereign Reality Visualization
 * Focus: $43,710,000.00 Manifestation
 */

import React, { useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stars, Text, Float } from '@react-three/drei';
import { ConductanceMonolith } from '../components/spire/ConductanceMonolith';
import { Shield, Zap, TrendingUp, MapPin } from 'lucide-react';

export const SpireDashboard: React.FC = () => {
  const [telemetry, setTelemetry] = useState({
    totalVerified: 26250000,
    conductance: 0.6005,
    status: 'ACCELERATING',
    timestamp: new Date().toISOString()
  });

  // 🛰️ Real-time Synchronization with the SOPHIA Brain (Port 3001)
  useEffect(() => {
    const fetchTelemetry = async () => {
      try {
        const response = await fetch('http://localhost:3001/api/audit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assets: { liquidCash: telemetry.totalVerified } })
        });
        const data = await response.json();
        setTelemetry(data);
      } catch (err) {
        console.warn("[!] SERVER OFFLINE: Using Cached Anchor");
      }
    };

    const interval = setInterval(fetchTelemetry, 5000);
    return () => clearInterval(interval);
  }, [telemetry.totalVerified]);

  return (
    <div className="relative w-full h-screen bg-zinc-950 text-white overflow-hidden font-mono">
      {/* --- LAYER 1: 3D SPATIAL ANCHOR --- */}
      <div className="absolute inset-0 z-0">
        <Canvas camera={{ position: [0, 5, 12], fov: 45 }}>
          <color attach="background" args={['#020202']} />
          <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
          <ambientLight intensity={0.2} />
          <pointLight position={[10, 10, 10]} intensity={1.5} color="#0070FF" />
          
          <Float speed={2} rotationIntensity={0.5} floatIntensity={1}>
            <ConductanceMonolith manifest={telemetry.totalVerified} />
          </Float>

          <OrbitControls enableZoom={true} enablePan={false} maxPolarAngle={Math.PI / 2} minPolarAngle={0} />
        </Canvas>
      </div>

      {/* --- LAYER 2: SOVEREIGN HUD --- */}
      <div className="absolute inset-0 pointer-events-none p-12 flex flex-col justify-between z-10">
        
        {/* Top Meta: Identity & Frequency */}
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <h1 className="text-4xl font-black italic tracking-tighter uppercase">The Spire</h1>
            <div className="flex items-center gap-2 text-blue-500/60 text-[10px] tracking-[0.3em] font-bold">
              <Shield className="w-3 h-3" /> SOVEREIGN NODE: AETHERIOS // KIN 207
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-zinc-500 tracking-widest uppercase mb-1">Status</div>
            <div className="px-4 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-[10px] font-black animate-pulse">
              {telemetry.status} // 0.999MHz
            </div>
          </div>
        </div>

        {/* Center: The Capital Pulse */}
        <div className="flex flex-col items-center justify-center">
          <div className="text-center space-y-4 bg-black/40 backdrop-blur-md p-8 rounded-3xl border border-white/5">
            <div className="text-[12px] text-blue-500/50 tracking-[1em] uppercase font-black">Verified Manifest</div>
            <div className="text-7xl font-black tracking-tighter text-white">
              ${telemetry.totalVerified.toLocaleString()}
            </div>
            <div className="w-full bg-zinc-900 h-1 rounded-full overflow-hidden">
               <div 
                className="h-full bg-blue-500 transition-all duration-1000 shadow-[0_0_20px_#0070FF]" 
                style={{ width: `${telemetry.conductance * 100}%` }} 
               />
            </div>
            <div className="text-[10px] text-zinc-500 tracking-[0.5em] uppercase">
              Resonance: {(telemetry.conductance * 100).toFixed(2)}%
            </div>
          </div>
        </div>

        {/* Bottom: Regional Metrics */}
        <div className="grid grid-cols-3 gap-8">
          <div className="bg-white/5 p-6 rounded-2xl border border-white/5 flex items-center gap-4 pointer-events-auto hover:bg-white/10 transition-all">
            <Zap className="text-blue-500" />
            <div>
              <div className="text-[8px] text-zinc-500 uppercase tracking-widest">Growth Pulse</div>
              <div className="text-xl font-bold">+ $250k</div>
            </div>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/5 flex items-center gap-4 pointer-events-auto hover:bg-white/10 transition-all">
            <TrendingUp className="text-green-500" />
            <div>
              <div className="text-[8px] text-zinc-500 uppercase tracking-widest">Kingscliff Target</div>
              <div className="text-xl font-bold">$43.71M</div>
            </div>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/5 flex items-center gap-4 pointer-events-auto hover:bg-white/10 transition-all">
            <MapPin className="text-red-500" />
            <div>
              <div className="text-[8px] text-zinc-500 uppercase tracking-widest">Physical Anchor</div>
              <div className="text-xl font-bold italic">Project Virtus</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SpireDashboard;